/**
 * 导出选项对话框：格式、缩放、JPG 质量
 */

import { useState } from 'react';
import { Button, Input, Label, ListBox, Modal, Select } from '@heroui/react';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export type ExportFormat = 'png' | 'jpeg';

export interface ExportOptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (format: ExportFormat, options: { scale: number; quality?: number }) => void;
  isExporting?: boolean;
}

const SCALE_OPTIONS = [1, 1.5, 2, 3] as const;

export function ExportOptionsDialog({
  open,
  onOpenChange,
  onExport,
  isExporting = false,
}: ExportOptionsDialogProps) {
  const [format, setFormat] = useState<ExportFormat>('png');
  const [scale, setScale] = useState<number>(2);
  const [quality, setQuality] = useState<number>(92);

  const handleExport = () => {
    onExport(format, {
      scale,
      quality: format === 'jpeg' ? quality / 100 : undefined,
    });
    onOpenChange(false);
  };

  return (
    <Modal isOpen={open} onOpenChange={onOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop isDismissable>
        <Modal.Container placement="center" scroll="inside" size="md">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>导出选项</Modal.Heading>
              <Modal.CloseTrigger aria-label="关闭" />
            </Modal.Header>
            <Modal.Body>
              <div className="grid gap-4 py-2">
                <div className="grid gap-2">
                  <Label className="text-xs">格式</Label>
                  <Select
                    aria-label="格式"
                    variant="secondary"
                    value={format}
                    onChange={(key) => {
                      if (key === 'png' || key === 'jpeg') setFormat(key);
                    }}
                  >
                    <Select.Trigger className="h-9">
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        <ListBox.Item id="png" textValue="PNG">
                          PNG
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                        <ListBox.Item id="jpeg" textValue="JPG">
                          JPG
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs">缩放倍数</Label>
                  <Select
                    aria-label="缩放倍数"
                    variant="secondary"
                    value={String(scale)}
                    onChange={(key) => {
                      if (key != null) setScale(Number(key));
                    }}
                  >
                    <Select.Trigger className="h-9">
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        {SCALE_OPTIONS.map((s) => (
                          <ListBox.Item key={s} id={String(s)} textValue={`${s}×`}>
                            {s}×
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </div>
                {format === 'jpeg' && (
                  <div className="grid gap-2">
                    <Label className="text-xs">JPG 质量（1–100）</Label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={quality}
                      onChange={(e) =>
                        setQuality(Math.max(1, Math.min(100, Number(e.target.value) || 92)))
                      }
                      className="h-9"
                    />
                  </div>
                )}
                <Button className="mt-2" onPress={handleExport} isDisabled={isExporting}>
                  {isExporting ? '导出中…' : '导出'}
                </Button>
              </div>
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
